import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { ArrowLeft, Lock, Unlock, CheckCircle, Star, BookOpen, Trophy, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const ChapterList = () => {
  const { subject } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchChapters(); }, [subject]);

  const fetchChapters = async () => {
    try {
      const res = await apiService.getChapters(subject);
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load chapters');
    } finally { setLoading(false); }
  };

  const subjectColors = {
    mathematics: { bg: 'from-blue-500 to-indigo-600', light: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-200' },
    science: { bg: 'from-green-500 to-emerald-600', light: 'bg-green-50', text: 'text-green-600', border: 'border-green-200' },
    english: { bg: 'from-purple-500 to-violet-600', light: 'bg-purple-50', text: 'text-purple-600', border: 'border-purple-200' },
  };
  const colors = subjectColors[subject] || subjectColors.mathematics;

  if (loading) return (
    <div className="min-h-screen bg-[#F4F8FB] flex items-center justify-center">
      <div className="w-16 h-16 border-4 border-[#4D96FF] border-t-transparent rounded-full animate-spin"></div>
    </div>
  );

  const chapters = data?.chapters || [];
  const passed = chapters.filter(c => c.passed).length;

  return (
    <div className="min-h-screen bg-[#F4F8FB]" data-testid="chapter-list-page">
      {/* Header */}
      <div className={`bg-gradient-to-r ${colors.bg}`}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 text-white/80 hover:text-white font-semibold mb-4" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />Back to Dashboard
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-black text-white capitalize">{subject} Chapters</h1>
              <p className="text-white/80 mt-1 text-lg">Grade {data?.grade} | {chapters.length} Chapters | Score 75%+ to unlock next</p>
            </div>
            <div className="bg-white/20 backdrop-blur-md rounded-2xl px-6 py-4 text-center border border-white/20">
              <p className="text-4xl font-black text-white">{passed}/{chapters.length}</p>
              <p className="text-xs text-white/80 font-semibold uppercase">Chapters Passed</p>
            </div>
          </div>
          {/* Progress Bar */}
          <div className="mt-6 bg-white/20 rounded-full h-4">
            <div className="bg-white rounded-full h-4 transition-all duration-500" style={{ width: `${(passed / chapters.length) * 100}%` }}></div>
          </div>
        </div>
      </div>

      {/* Chapters */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-4">
          {chapters.map((ch, idx) => (
            <ChapterCard key={ch.id} chapter={ch} index={idx} colors={colors} subject={subject}
              onTakeTest={() => navigate(`/chapters/${subject}/${ch.id}/test`)} />
          ))}
        </div>
      </div>
    </div>
  );
};

const ChapterCard = ({ chapter, index, colors, subject, onTakeTest }) => {
  const navigate = useNavigate();
  const isPassed = chapter.passed;
  const isLocked = !chapter.unlocked;

  return (
    <div data-testid={`chapter-card-${chapter.id}`}
      className={`bg-white rounded-2xl border-2 overflow-hidden transition-all duration-300 ${
        isLocked ? 'opacity-60 border-slate-200' : isPassed ? `${colors.border} hover:-translate-y-1 hover:shadow-xl` : 'border-slate-200 hover:-translate-y-1 hover:shadow-xl'
      }`}>
      <div className="flex items-stretch">
        {/* Chapter Number */}
        <div className={`w-20 flex flex-col items-center justify-center flex-shrink-0 ${
          isPassed ? `bg-gradient-to-b ${colors.bg.replace('to-r', 'to-b')} text-white` : isLocked ? 'bg-slate-100 text-slate-400' : `${colors.light} ${colors.text}`
        }`}>
          <span className="text-3xl font-black">{chapter.chapter_number}</span>
          {isPassed && <CheckCircle className="w-5 h-5 mt-1" />}
          {isLocked && <Lock className="w-5 h-5 mt-1" />}
        </div>

        {/* Content */}
        <div className="flex-1 p-5">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className={`text-xl font-bold ${isLocked ? 'text-slate-400' : 'text-[#1E293B]'}`}>{chapter.name}</h3>
                {chapter.book && (
                  <span className={`text-[10px] px-2 py-0.5 rounded-lg font-bold uppercase tracking-wide ${isLocked ? 'bg-slate-100 text-slate-400' : 'bg-[#FFC03D]/20 text-[#E6A820]'}`}>{chapter.book}</span>
                )}
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {chapter.topics.map((t, i) => (
                  <span key={i} className={`text-xs px-3 py-1 rounded-full font-semibold ${isLocked ? 'bg-slate-100 text-slate-400' : `${colors.light} ${colors.text}`}`}>{t}</span>
                ))}
              </div>
              {chapter.attempts > 0 && (
                <div className="mt-3 flex items-center gap-4 text-sm">
                  <span className="text-[#64748B]">Best Score: <strong className={chapter.best_score >= 75 ? 'text-green-600' : 'text-orange-500'}>{chapter.best_score}%</strong></span>
                  <span className="text-[#64748B]">Attempts: <strong>{chapter.attempts}</strong></span>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="ml-4 flex-shrink-0 flex flex-col gap-2">
              {isLocked ? (
                <div className="px-5 py-3 bg-slate-100 text-slate-400 rounded-2xl font-bold text-sm flex items-center gap-2">
                  <Lock className="w-4 h-4" /> Locked
                </div>
              ) : (
                <>
                  <button onClick={() => navigate(`/chapters/${subject}/${chapter.id}/learn`)} data-testid={`learn-${chapter.id}`}
                    className="px-5 py-2.5 bg-gradient-to-r from-[#FFC03D] to-orange-500 text-white rounded-2xl font-bold text-sm hover:scale-105 transition-all shadow-lg flex items-center gap-2 border-b-3 border-black/20 active:border-b-0 active:translate-y-0.5">
                    <BookOpen className="w-4 h-4" /> Learn with AI
                  </button>
                  {isPassed ? (
                    <button onClick={onTakeTest} className="px-5 py-2 bg-green-100 text-green-700 rounded-2xl font-bold text-sm hover:bg-green-200 transition-all flex items-center gap-2" data-testid={`retake-${chapter.id}`}>
                      <Trophy className="w-4 h-4" /> Passed! Retake
                    </button>
                  ) : (
                    <button onClick={onTakeTest} data-testid={`take-test-${chapter.id}`}
                      className={`px-5 py-2.5 bg-gradient-to-r ${colors.bg} text-white rounded-2xl font-bold text-sm hover:scale-105 transition-all shadow-lg flex items-center gap-2 border-b-3 border-black/20 active:border-b-0 active:translate-y-0.5`}>
                      <Star className="w-4 h-4" /> Take Test
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChapterList;
